import { useCallback, useRef } from "react";
import { type SurveyModel, type CompletingEvent } from "survey-core";
import { type SocietyWorkflowInstanceInvokeParamsInput } from "shared/api/types/society/[societyId]/workflow/[workflowId]/instance";
import { usePillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import AdminFormSubmitModal from "shared/components/survey-js-form/forms/AdminFormSubmitModal";
import { useCustomSession } from "shared/context/CustomSessionContext";

export const useSurveyJSOnCompletingFunction = () => {
  const session = useCustomSession();
  const adminFormSubmitControl =
    usePillarModalControl<SocietyWorkflowInstanceInvokeParamsInput>();

  const adminOptionsRef =
    useRef<SocietyWorkflowInstanceInvokeParamsInput | null>(null);

  // clear on read so the next submit prompts again
  const consumeAdminOptions = useCallback(() => {
    const currentAdminOptions = adminOptionsRef.current;
    adminOptionsRef.current = null;
    return currentAdminOptions;
  }, []);

  const onCompleting = useCallback(
    (sender: SurveyModel, options: CompletingEvent) => {
      if (!session.adminMode) {
        return;
      }
      if (adminOptionsRef.current !== null) {
        return;
      }
      options.allowComplete = false;
      adminFormSubmitControl
        .openAsync()
        .then((result) => {
          adminOptionsRef.current = result;
          sender.doComplete();
        })
        .catch(() => {
          adminOptionsRef.current = null;
        });
    },
    [session.adminMode, adminFormSubmitControl, adminOptionsRef],
  );

  const SurveyJSAdminOnCompletingModal = useCallback(
    () => <AdminFormSubmitModal control={adminFormSubmitControl} />,
    [adminFormSubmitControl],
  );

  return { onCompleting, SurveyJSAdminOnCompletingModal, consumeAdminOptions };
};
